/**
 * measureConditionsCore.js — УСЛОВИЯ, В КОТОРЫХ СДЕЛАН ЗАМЕР. [12.0.23]
 *
 * ЗАЧЕМ. Длина, снятая линейкой, сама по себе не говорит, можно ли её
 * сравнивать с прошлым замером. Один и тот же узел даёт разный диаметр:
 *
 *   · на нативном срезе и на реформате, поставленном МЕЖДУ срезами (там край
 *     опухоли — смесь двух соседних срезов);
 *   · на тонком срезе и на MIP-слэбе в несколько миллиметров (край
 *     размывается, и узел «растёт»);
 *   · на наклонённой серии в режиме «по съёмке» и «по телу» (во втором случае
 *     аксиальный реформат режет опухоль под другим углом, чем снимал аппарат).
 *
 * Через месяц по готовой линейке этого уже не восстановить. Поэтому условия
 * записываются В САМ ЗАМЕР в момент его создания и хранятся вместе с ним.
 *
 * Модуль чистый — без браузера и Cornerstone, только правила и подписи. Их
 * проверяют тесты; всё, что трогает окна, живёт в measureConditions.js.
 *
 * EXPORTS: РЕЖИМЫ_СЛЭБА, ПОРОГИ, измерительныйЛи, оценитьРиск, подписьУсловий,
 *          ИМЕНА_ИНСТРУМЕНТОВ, значениеЗамера
 */

/**
 * Режим смешивания слэба — номера из cornerstone.Enums.BlendModes.
 * Номер, а не имя: перечисление Cornerstone числовое, и так его отдаёт
 * viewport.getBlendMode().
 */
export const РЕЖИМЫ_СЛЭБА = Object.freeze({
  0: 'обычный',        // COMPOSITE — тонкий срез
  1: 'MIP',            // MAXIMUM_INTENSITY_BLEND
  2: 'MinIP',          // MINIMUM_INTENSITY_BLEND
  3: 'среднее',        // AVERAGE_INTENSITY_BLEND
});

/**
 * Пороги предупреждений. Каждое число — с происхождением.
 */
export const ПОРОГИ = Object.freeze({
  /**
   * MIP/MinIP/среднее толще 1 мм уже меняет край: это толщина типичного
   * тонкого среза КТ и МРТ для планирования. Тоньше — проекция совпадает со
   * срезом в пределах одного вокселя.
   */
  слэбПроекцииМм: 1,
  /**
   * Обычный (композитный) слэб толще 3 мм — это уже «толстый срез»: втрое
   * толще типичного тонкого среза для планирования (1 мм), и край мелкого
   * очага (метастазы 3–5 мм) усредняется по толщине заметно. Число ПОДОБРАНО
   * по этому соображению, а не взято из стандарта — правьте по опыту.
   */
  толстыйСлэбМм: 3,
  /**
   * Наклон больше 2° — тот же порог, что программа уже использует, решая,
   * наклонена ли серия (viewport.js, сообщение «[MPR] Серия наклонена»).
   * Ниже двух градусов разница диаметров — доли процента.
   */
  наклонГрад: 2,
});

/** Инструменты, результат которых — размер, площадь, угол или плотность. */
const ИЗМЕРИТЕЛЬНЫЕ = /Length|Bidirectional|ROI|Angle|Cobb|Probe|Freehand|Spline|Livewire|Height/i;

/** Стрелки и надписи — пометки, а не замеры: условия им не нужны. */
const ПОМЕТКИ = /Arrow|Text|Annotate|Label/i;

/** Замер ли это, для которого условия имеют значение. */
export function измерительныйЛи(имяИнструмента) {
  const и = String(имяИнструмента || '');
  return ИЗМЕРИТЕЛЬНЫЕ.test(и) && !ПОМЕТКИ.test(и);
}

/**
 * Оценить условия замера.
 *
 * @param {object} у — условия (см. measureConditions.условияОкна)
 * @param {'нативный срез'|'реформат'|'3D'} у.вид
 * @param {'по съёмке'|'по телу'} [у.выравнивание]
 * @param {number} [у.слэбМм]
 * @param {string} [у.режимСлэба]
 * @param {number} [у.наклонГрад]
 * @returns {{ уровень: 'норма'|'осторожно', причины: string[] }}
 */
export function оценитьРиск(у) {
  const причины = [];
  if (!у || у.вид === 'нативный срез') return { уровень: 'норма', причины };

  const слэб = Number(у.слэбМм) || 0;
  const проекция = у.режимСлэба && у.режимСлэба !== 'обычный';

  if (проекция && слэб > ПОРОГИ.слэбПроекцииМм) {
    причины.push(`${у.режимСлэба}-слэб ${слэб.toFixed(1)} мм: край опухоли размыт, `
      + 'размер обычно завышен — мерьте на тонком срезе');
  } else if (!проекция && слэб > ПОРОГИ.толстыйСлэбМм) {
    причины.push(`толстый слэб ${слэб.toFixed(1)} мм: край опухоли усреднён по толщине`);
  }

  const наклон = Number(у.наклонГрад) || 0;
  if (у.выравнивание === 'по телу' && наклон > ПОРОГИ.наклонГрад) {
    причины.push(`серия наклонена на ${наклон.toFixed(0)}°, выравнивание «по телу»: `
      + 'реформат режет опухоль под другим углом, чем снимал аппарат — '
      + 'переключите на «по съёмке» или мерьте на нативном срезе');
  }

  return { уровень: причины.length ? 'осторожно' : 'норма', причины };
}

/**
 * Короткая подпись условий — одна строка под замером.
 * Пример: «реформат · аксиальный · по съёмке · слэб 0.1 мм · наклон 9°».
 */
export function подписьУсловий(у) {
  if (!у) return 'условия не записаны';
  if (у.вид === 'нативный срез') {
    return у.плоскость ? `нативный срез · ${у.плоскость}` : 'нативный срез';
  }
  const части = [у.вид];
  if (у.плоскость) части.push(у.плоскость);
  if (у.выравнивание) части.push(у.выравнивание);
  const слэб = Number(у.слэбМм);
  if (Number.isFinite(слэб)) {
    части.push(у.режимСлэба && у.режимСлэба !== 'обычный'
      ? `${у.режимСлэба} ${слэб.toFixed(1)} мм`
      : `слэб ${слэб.toFixed(1)} мм`);
  }
  const наклон = Number(у.наклонГрад);
  if (Number.isFinite(наклон)) части.push(`наклон ${наклон.toFixed(0)}°`);
  return части.join(' · ');
}

/** Русские имена инструментов для списка. */
export const ИМЕНА_ИНСТРУМЕНТОВ = Object.freeze({
  Length: 'Длина',
  Bidirectional: 'Два диаметра',
  EllipticalROI: 'Эллипс',
  CircleROI: 'Круг',
  RectangleROI: 'Прямоугольник',
  PlanarFreehandROI: 'Контур',
  SplineROI: 'Сплайн',
  LivewireContour: 'Контур',
  Angle: 'Угол',
  CobbAngle: 'Угол Кобба',
  Probe: 'Точка',
  Height: 'Высота',
});

/**
 * Главное число замера для списка — из cachedStats Cornerstone.
 *
 * cachedStats хранится по окнам (ключ — targetId), поэтому берём первую
 * запись, где есть число: у одного замера она одна.
 */
/** Cornerstone пишет единицы латиницей; в русском списке — по-русски. */
const ЕДИНИЦЫ = Object.freeze({ mm: 'мм', 'mm²': 'мм²', mm2: 'мм²', px: 'пкс', 'px²': 'пкс²', HU: 'HU' });
const _единица = (е, запас) => ЕДИНИЦЫ[е] || е || запас;

export function значениеЗамера(аннотация) {
  const статы = аннотация?.data?.cachedStats;
  if (!статы || typeof статы !== 'object') return '';
  for (const с of Object.values(статы)) {
    if (!с || typeof с !== 'object') continue;
    const мм = (v, ед = 'мм') => `${Number(v).toFixed(1)} ${ед}`;
    if (Number.isFinite(с.length) && Number.isFinite(с.width)) return `${мм(с.length)} × ${мм(с.width)}`;
    if (Number.isFinite(с.length)) return мм(с.length, _единица(с.unit, 'мм'));
    if (Number.isFinite(с.area)) return мм(с.area, _единица(с.areaUnit, 'мм²'));
    if (Number.isFinite(с.angle)) return `${Number(с.angle).toFixed(1)}°`;
    if (Number.isFinite(с.value)) return `${Number(с.value).toFixed(0)} HU`;
  }
  return '';
}
